
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { OnboardingProvider } from "@/hooks/useOnboarding";
import { AuthProvider } from "@/hooks/useAuth";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { useEffect, useState } from "react";
import Index from "./pages/Index";
import Onboarding from "./pages/Onboarding";
import NotFound from "./pages/NotFound";

// Initialize query client with retry logic for mobile networks
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      retryDelay: attempt => Math.min(1000 * 2 ** attempt, 30000),
      staleTime: 1000 * 60 * 5, // 5 minutes
      // Add additional options for mobile browsers
      refetchOnWindowFocus: false, // Disable refetch on focus for mobile
      networkMode: 'always', // Be less aggressive with offline handling
    }
  }
});

// Component to handle browser-specific issues
const BrowserCompatibility = () => {
  const [isReady, setIsReady] = useState(false);
  
  useEffect(() => {
    try {
      // Detect Chrome on Android
      const isAndroid = /android/i.test(navigator.userAgent);
      const isChrome = /chrome/i.test(navigator.userAgent);
      
      if (isAndroid && isChrome) {
        console.log('Chrome on Android detected, using optimized display');
        // Implement mobile-specific adjustments
        document.documentElement.style.height = '100%';
        document.body.style.height = '100%';
        document.body.style.overflow = 'auto';
        document.getElementById('root')?.setAttribute('style', 'height: 100%; display: flex; flex-direction: column;');
      }
      
      // Handle visibility changes for better mobile experience
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          console.log('App is visible again, refreshing data...');
          // Force a re-render to reload saved state when returning to the app
          window.dispatchEvent(new Event('storage'));
        }
      };
      
      document.addEventListener('visibilitychange', handleVisibilityChange);
      
      // Mark as ready after initial setup
      setIsReady(true);
      
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    } catch (error) {
      console.error("Error in browser compatibility setup:", error);
      // Still mark as ready to not block rendering
      setIsReady(true);
      return () => {};
    }
  }, []);
  
  return null;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <BrowserRouter>
        <AuthProvider>
          <OnboardingProvider>
            <BrowserCompatibility />
            <Toaster />
            <Sonner />
            <Routes>
              <Route path="/" element={<Index />} />
              <Route 
                path="/onboarding" 
                element={
                  <ProtectedRoute>
                    <Onboarding />
                  </ProtectedRoute>
                } 
              />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </OnboardingProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
